//@flow
import * as React from 'react'
import classnames from 'classnames'
import { useState, useRef } from 'react'

import { useFilterStyles, useTagStyles } from './styles'
import { useKeyPress } from '~/core/utils/hooks'
import { useI18n } from '~/core/i18n'

import { TableFilterTooltip } from './'
import { Filter } from './Filter'

type FilterTagProps = {
  filter: Filter,
  onEdit: () => void,
  onRemove: () => void,
  onToggleDisabled: () => void
}

const FilterTag = ({
  filter: { id, column, value, isDisabled },
  onEdit,
  onRemove,
  onToggleDisabled,
  ...props
}: FilterTagProps) => {
  const [msg] = useI18n()
  const classes = useTagStyles()

  return (
    <div className={classes.tagWrapper} {...props}>
      <div className={classnames(classes.tagValue, { disabled: isDisabled })}>
        <span data-tag="label">@{column.toLowerCase().replace(/\s/g, '_')}:</span>
        &nbsp;
        <strong>{value.replace(/(.{12})..+/, '$1…')}</strong>
      </div>
      {/* Initial style fix - set opacity to 0 before loading classes */}
      <div className={classes.tagOverlay} style={{ opacity: 0 }}>
        <span data-tag="edit" id={`filter-edit-${id}`} title={msg('common.table.filter.edit')} onClick={onEdit}>
          <i className="icon icon-s">edit</i>
        </span>
        <span
          className={classnames({
            disabled: isDisabled
          })}
          data-tag="toggle-disabled"
          id={`filter-edit-${id}`}
          title={isDisabled ? msg('common.table.filter.enable') : msg('common.table.filter.disable')}
          onClick={onToggleDisabled}
        >
          <i className="icon icon-s">eye</i>
        </span>
        <span data-tag="remove" id={`filter-remove-${id}`} title={msg('common.table.filter.delete')} onClick={onRemove}>
          <i className="icon icon-s">close</i>
        </span>
      </div>
    </div>
  )
}

type TableFilterProps = {
  columns: any[],
  filters: Filter[],
  onChange: (filters: Filter[]) => void
}

const TableFilter = ({ columns, filters, onChange, ...props }: TableFilterProps) => {
  const [{ isActive, editingId }, setTooltipStatus] = useState({
    isActive: false,
    editingId: ''
  })
  const [msg] = useI18n()
  const tooltipRef = useRef()
  const classes = useFilterStyles()

  const handleToggleTooltip = isActive =>
    setTooltipStatus({
      editingId: '',
      isActive
    })

  const handleEditFilter = id =>
    setTooltipStatus({
      editingId: id,
      isActive: true
    })

  const handleRemoveFilter = (id: string) => {
    const newFilters = filters.filter(filter => filter.id !== id)
    return onChange(newFilters)
  }

  const handleToggleFilterDisabled = (id: string) => {
    const newFilters = filters.map(filter =>
      filter.id === id ? new Filter({ ...filter, isDisabled: !filter.isDisabled }) : filter
    )
    return onChange(newFilters)
  }

  const handleUpdateFilters = ({ id, filter: newFilter }) => {
    if (id) {
      const newFilters = filters.map(filter => (filter.id === id ? newFilter : filter))
      return onChange(newFilters)
    } else {
      return onChange([...filters, newFilter])
    }
  }

  useKeyPress('Escape', () => handleToggleTooltip(false), isActive)

  return (
    <div className={classes.tableFilterWrapper} {...props}>
      <button data-btn="add" onClick={() => handleToggleTooltip(true)}>
        + {msg('common.table.filter.add')}
      </button>
      <div className={classes.tableFilterTagsContainer}>
        {filters.map(filter => (
          <FilterTag
            key={filter.id}
            filter={filter}
            onEdit={() => handleEditFilter(filter.id)}
            onRemove={() => handleRemoveFilter(filter.id)}
            onToggleDisabled={() => handleToggleFilterDisabled(filter.id)}
          />
        ))}
      </div>
      {isActive && (
        <div ref={tooltipRef} className={classes.tableFilterTooltipContainer}>
          <header className={classes.tableFilterTooltipHeader}>
            <span>{editingId ? msg('common.table.filter.edit') : msg('common.table.filter.create')}</span>
            <i className="icon icon-s" data-icon="close" onClick={() => handleToggleTooltip(false)}>
              close
            </i>
          </header>
          <div className={classes.tableFilterTooltipContent}>
            <TableFilterTooltip
              columnsOptions={columns
                .filter(({ isFilterable }) => isFilterable !== false)
                .map(column => ({
                  value: column.accessor,
                  label: column.Header,
                  allowedComparisms: column.allowedComparisms
                }))}
              filter={(editingId && filters.find(({ id }) => id === editingId)) || null}
              id="syslog-filter-tooltip"
              onChange={filter => {
                handleToggleTooltip(false)
                handleUpdateFilters({ id: editingId, filter })
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TableFilter
